import { BdSdk } from "@blockdaemon/bd-sdk";

import { getCachedWidgetConfig } from "../../features/customization/utils/config-loader";

const SOLANA_MAINNET_CAIP_PREFIX = "solana:5eykt4";
const ETHEREUM_MAINNET_CAIP = "eip155:1";

interface ValidatorAddress {
  chainId: string;
  address: string;
}

let _sdk: BdSdk | undefined;

function isMainnet(v: ValidatorAddress): boolean {
  return v.chainId.startsWith(SOLANA_MAINNET_CAIP_PREFIX);
}

function buildSolanaProtocolConfig(addresses: ValidatorAddress[]) {
  if (addresses.length === 0) return undefined;

  return {
    customerMainnetValidatorVotePubKeys: addresses.filter(isMainnet).map((v) => v.address),
    customerTestnetValidatorVotePubKeys: addresses
      .filter((v) => !isMainnet(v))
      .map((v) => v.address),
  };
}

function buildEthereumProtocolConfig(addresses: ValidatorAddress[]) {
  if (addresses.length === 0) return undefined;

  return {
    customerMainnetVaultAddresses: addresses
      .filter((v) => v.chainId === ETHEREUM_MAINNET_CAIP)
      .map((v) => v.address),
    customerTestnetVaultAddresses: addresses
      .filter((v) => v.chainId !== ETHEREUM_MAINNET_CAIP)
      .map((v) => v.address),
  };
}

function getSDK(): BdSdk {
  if (_sdk) {
    return _sdk;
  }

  const stakingConfig = getCachedWidgetConfig()?.features?.staking;
  const referralTag = stakingConfig?.ethereum?.referralTag;

  const solanaAddresses =
    stakingConfig?.solana?.enabled === false
      ? undefined
      : stakingConfig?.solana?.validatorAddresses;

  const ethAddresses =
    stakingConfig?.ethereum?.enabled === false
      ? undefined
      : stakingConfig?.ethereum?.vaultAddresses;

  const solanaProtocolConfig = buildSolanaProtocolConfig(solanaAddresses ?? []);
  const ethProtocolConfig = buildEthereumProtocolConfig(ethAddresses ?? []);

  const protocolConfig =
    solanaProtocolConfig || ethProtocolConfig
      ? {
          ...(solanaProtocolConfig ? { solana: solanaProtocolConfig } : {}),
          ...(ethProtocolConfig ? { ethereum: ethProtocolConfig } : {}),
        }
      : undefined;

  _sdk = new BdSdk({
    basePath: `${window.location.origin}/proxy`,
    configuration: {
      protocolConfig,
      ...(referralTag ? { referralTag } : {}),
    },
  });

  return _sdk;
}

export const blockdaemonSDK = new Proxy({} as BdSdk, {
  get(_target, prop) {
    const sdk = getSDK();
    // biome-ignore lint/suspicious/noExplicitAny: Proxy requires dynamic property access
    const value = (sdk as any)[prop];
    return typeof value === "function" ? value.bind(sdk) : value;
  },
});
